import { type FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { type AuthConfig, useAuthStore } from '@/core/auth/auth-store';
import { useRuntimeConfigStore } from '@/core/config/runtime-config.loader';

import styles from './Landing.module.scss';

function providerKey(provider: string): string {
    if (provider === 'google') return 'google_oauth2';
    return provider;
}

function prettyProvider(provider: string): string {
    if (provider === 'google_oauth2' || provider === 'google') return 'Google';
    return provider;
}

export function Landing() {
    const fetchConfig = useAuthStore((s) => s.fetchConfig);
    const authBaseUrl = useRuntimeConfigStore((s) => s.config?.authBaseUrl ?? null);
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [config, setConfig] = useState<AuthConfig | null>(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    useEffect(() => {
        let cancelled = false;
        fetchConfig()
            .then((cfg) => {
                if (cancelled) return;
                setConfig(cfg);
            })
            .catch(() => {
                if (cancelled) return;
                setError('Failed to load auth configuration.');
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fetchConfig]);

    const startOAuth = (provider: string) => {
        // null guards "runtime config not loaded yet"; empty string is a
        // valid same-origin value in production.
        if (authBaseUrl === null) return;
        const key = providerKey(provider);
        const suffix = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
        globalThis.location.assign(`${authBaseUrl}/auth/${key}${suffix}`);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    };

    const providers = config?.providers ?? [];
    const authMode = config?.auth_mode ?? null;

    return (
        <div className={styles['authPage']}>
            <div className={styles['authCard']}>
                <header className={styles['authHeader']}>
                    <h1>Identifi Partner Force</h1>
                    <p className={styles['subtitle']}>Sign in to continue</p>
                </header>

                {loading && <div className={`${styles['state']} ${styles['stateLoading']}`}>Loading…</div>}

                {!loading && error && (
                    <div className={`${styles['state']} ${styles['stateError']}`}>{error}</div>
                )}

                {!loading && !error && (
                    <>
                        <form className={styles['authForm']} onSubmit={handleSubmit}>
                            <div className={styles['field']}>
                                <label htmlFor="email">Username / Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    name="email"
                                    autoComplete="email"
                                    placeholder="name@company.com"
                                />
                            </div>

                            <div className={styles['field']}>
                                <label htmlFor="password">Password</label>
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    name="password"
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                />
                            </div>

                            <button
                                className={`${styles['btn']} ${styles['btnPrimary']}`}
                                type="submit"
                                disabled
                            >
                                Sign in (coming soon)
                            </button>
                        </form>

                        <div className={styles['divider']}>
                            <span>or</span>
                        </div>

                        <div className={styles['providers']}>
                            {providers.map((provider) => (
                                <button
                                    key={provider}
                                    className={`${styles['btn']} ${styles['btnSecondary']}`}
                                    type="button"
                                    onClick={() => startOAuth(provider)}
                                >
                                    Continue with {prettyProvider(provider)}
                                </button>
                            ))}

                            {providers.length === 0 && (
                                <div className={`${styles['state']} ${styles['stateMuted']}`}>
                                    No auth providers are available.
                                </div>
                            )}
                        </div>

                        {authMode && (
                            <div className={styles['footnote']}>
                                Auth mode: <span className={styles['mono']}>{authMode}</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
